// Navegacion por teclado para todo el panel (un solo listener global):
//  - Flechas: mueven el foco al siguiente/anterior elemento (botones, campos, links).
//  - Enter en un campo: pasa al siguiente campo (si el campo no tiene su propio Enter).
//  - Escape: cierra el modal/panel que este encima (su boton Cancelar / Cerrar / X).
// ponytail: navegacion lineal (orden del DOM), no espacial; si en rejillas de
// productos se necesita arriba/abajo por filas, agregar calculo por posicion.

const FOCUSABLE = 'button:not([disabled]), a[href], input:not([disabled]):not([type=hidden]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
const CAMPOS = 'input:not([disabled]):not([type=hidden]):not([type=checkbox]):not([type=radio]), select:not([disabled]), textarea:not([disabled])';

const visible = (el: HTMLElement) => el.offsetParent !== null || el.getClientRects().length > 0;

// El modal abierto es el ultimo overlay fijo de pantalla completa; si no hay, todo el documento.
function ambito(): ParentNode {
  const overlays = Array.from(document.querySelectorAll<HTMLElement>('div[style*="position: fixed"]'))
    .filter(el => visible(el) && el.querySelector(FOCUSABLE));
  return overlays[overlays.length - 1] ?? document;
}

function mover(desde: Element | null, paso: 1 | -1, selector = FOCUSABLE) {
  const lista = Array.from(ambito().querySelectorAll<HTMLElement>(selector)).filter(visible);
  if (!lista.length) return false;
  const i = desde ? lista.indexOf(desde as HTMLElement) : -1;
  const sig = lista[i === -1 ? (paso === 1 ? 0 : lista.length - 1) : i + paso];
  if (!sig) return false;
  sig.focus();
  return true;
}

// Si React ya le puso onKeyDown al elemento, respetamos su propio manejo.
function tieneOnKeyDown(el: Element) {
  const k = Object.keys(el).find(k => k.startsWith('__reactProps'));
  return !!(k && (el as any)[k]?.onKeyDown);
}

function cerrarModal() {
  const zona = ambito();
  if (zona === document) return false;
  const botones = Array.from(zona.querySelectorAll<HTMLElement>('button')).filter(visible);
  const cerrar = botones.find(b => /^(✕|×|x|cancelar|cerrar)$/i.test(b.textContent?.trim() ?? ''))
    ?? botones.find(b => b.querySelector('svg.lucide-x'));
  cerrar?.click();
  return !!cerrar;
}

export function manejarTecla(e: KeyboardEvent) {
  if (e.defaultPrevented || e.altKey || e.ctrlKey || e.metaKey) return;
  const el = document.activeElement as HTMLElement | null;
  const tag = el?.tagName;
  const tipo = (el as HTMLInputElement | null)?.type;
  const escribiendo = tag === 'TEXTAREA' || tag === 'SELECT' || (tag === 'INPUT' && !['checkbox', 'radio', 'button', 'submit'].includes(tipo ?? ''));

  if (e.key === 'Escape') {
    if (cerrarModal()) e.preventDefault();
    return;
  }

  if (e.key === 'Enter' && tag === 'INPUT' && escribiendo && el && !tieneOnKeyDown(el)) {
    if (mover(el, 1, CAMPOS)) e.preventDefault();
    return;
  }

  if (!e.key.startsWith('Arrow') || tieneOnKeyDown(el ?? document.body)) return;
  // En campos de texto las flechas izq/der mueven el cursor; arriba/abajo cambian de campo
  // (excepto textarea, select y numeros, donde tienen su uso normal).
  if (escribiendo && (tag !== 'INPUT' || tipo === 'number' || e.key === 'ArrowLeft' || e.key === 'ArrowRight')) return;
  const paso = e.key === 'ArrowDown' || e.key === 'ArrowRight' ? 1 : -1;
  if (mover(el && el !== document.body ? el : null, paso)) e.preventDefault();
}

export function activarTeclado() {
  document.addEventListener('keydown', manejarTecla);
}
